import { API } from '../../utils/amplifyWrapper';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from 'react-toastify';

// Fetch single integration
export const fetchIntegration = async (org_id: string, integration_id: string) => {
    const response = await API.get('main', `/organisation/${org_id}/integration/${integration_id}`, {
        headers: {'Content-Type': "application/json"}
    });
    return response.data;
}

interface IUseIntegrationProps {
    org_id: string
    integration_id: string
}

export const useIntegration = ({org_id, integration_id}: IUseIntegrationProps) => {
    return useQuery(
        ['integration', org_id, integration_id], 
        () => fetchIntegration(org_id, integration_id),
        {
            enabled: !!(org_id && integration_id),
            staleTime: 5 * 60 * 1000, // 5 minutes - integration data doesn't change often
        }
    )
}

// Fetch all integrations for an organization
export const fetchIntegrations = async (org_id: string) => {
    const response = await API.get('main', `/organisation/${org_id}/integration`, {
        headers: {'Content-Type': "application/json"}
    });
    return response.data;
}

interface IUseIntegrationsProps {
    org_id: string
}

export const useIntegrations = ({org_id}: IUseIntegrationsProps) => {
    return useQuery(
        ['integrations', org_id], 
        () => fetchIntegrations(org_id),
        {
            enabled: !!org_id,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    )
}

// Create Integration
interface ICreateIntegrationBody {
    integration_name: string
    lang_code?: string
    player_settings?: {
        player_type: string
    }
    audio_profile?: {
        platform: string
        engine: string
        voice: string
        volume: number
        speed: number
        pitch?: string
        style?: string
        auto_breathing?: boolean
    }
}

export const createIntegration = async ({ org_id, body }: { org_id: string, body: ICreateIntegrationBody }) => {
    try {
        // Use /default endpoint for simple creation, /integration for full creation with audio profile
        const endpoint = body.audio_profile 
            ? `/organisation/${org_id}/integration`
            : `/organisation/${org_id}/integration/default`;
            
        const res = await API.post(`main`, endpoint, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        
        toast.success('Project has been created successfully!');
        
        return res.data;
    } catch (error: any) {
        // Handle different error structures from AWS Amplify API
        const msg = error?.response?.data?.message 
            || error?.response?.message 
            || error?.message 
            || "Something went wrong while creating the project.";
        toast.error(msg);
        throw error;
    }
}

interface IUseCreateIntegrationProps {
    org_id: string
}

export const useCreateIntegration = ({ org_id }: IUseCreateIntegrationProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: ICreateIntegrationBody) => createIntegration({ org_id, body }),
        onSuccess: () => {
            // Invalidate integrations list to refetch
            queryClient.invalidateQueries(['integrations', org_id]);
        }
    });
}

// Update Integration General Settings
export const updateIntegrationGeneral = async ({ org_id, integration_id, body }: { org_id: string, integration_id: string, body: any }) => {
    try {
        await API.put(`main`, `/organisation/${org_id}/integration/${integration_id}/general_settings`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('General settings have been saved successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while saving the General Settings.");
        throw error;
    }
}

interface IUseUpdateIntegrationGeneralProps {
    org_id: string
    integration_id: string
}

export const useUpdateIntegrationGeneral = ({ org_id, integration_id }: IUseUpdateIntegrationGeneralProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: any) => updateIntegrationGeneral({ org_id, integration_id, body }),
        onSuccess: () => {
            // Invalidate integration data to refetch
            queryClient.invalidateQueries(['integration', org_id, integration_id]);
        }
    });
}

// Update Integration Audio Player Settings
export const updateIntegrationPlayer = async ({ org_id, integration_id, body }: { org_id: string, integration_id: string, body: any }) => {
    try {
        await API.put(`main`, `/organisation/${org_id}/integration/${integration_id}/audio_player_settings`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Audio Player settings have been saved successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while saving the Audio Player settings.");
        throw error;
    }
}

interface IUseUpdateIntegrationPlayerProps {
    org_id: string
    integration_id: string
}

export const useUpdateIntegrationPlayer = ({ org_id, integration_id }: IUseUpdateIntegrationPlayerProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: any) => updateIntegrationPlayer({ org_id, integration_id, body }),
        onSuccess: () => {
            // Invalidate integration data to refetch
            queryClient.invalidateQueries(['integration', org_id, integration_id]);
        }
    });
}

// Update Integration Audio Profile
export const updateIntegrationVoice = async ({ org_id, integration_id, body }: { org_id: string, integration_id: string, body: any }) => {
    try {
        await API.put(`main`, `/organisation/${org_id}/integration/${integration_id}/audio_profile`, {
            body,
            headers: { "Content-Type": "application/json" }
        });
        toast.success('Audio Profile has been updated successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while saving the Audio Profile.");
        throw error;
    }
}

interface IUseUpdateIntegrationVoiceProps {
    org_id: string
    integration_id: string
}

export const useUpdateIntegrationVoice = ({ org_id, integration_id }: IUseUpdateIntegrationVoiceProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (body: any) => updateIntegrationVoice({ org_id, integration_id, body }),
        onSuccess: () => {
            // Invalidate integration data to refetch
            queryClient.invalidateQueries(['integration', org_id, integration_id]);
        }
    });
}

// Delete Integration
export const deleteIntegration = async ({ org_id, integration_id }: { org_id: string, integration_id: string }) => {
    try {
        await API.del('main', `/organisation/${org_id}/integration/${integration_id}`);
        toast.success('Project has been deleted successfully!');
    } catch (error: any) {
        toast.error("Something went wrong while deleting the project.");
        throw error;
    }
}

interface IUseDeleteIntegrationProps {
    org_id: string
}

export const useDeleteIntegration = ({ org_id }: IUseDeleteIntegrationProps) => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (integration_id: string) => deleteIntegration({ org_id, integration_id }),
        onSuccess: () => {
            // Invalidate integrations list to refetch
            queryClient.invalidateQueries(['integrations', org_id]);
        }
    });
}

